import { ltr, num } from '../format';
import * as learning from './iris-learning';

/**
 * Iris in agent mode (components/play/Iris.tsx, docs/iris.md "The panel").
 *
 * Her own file rather than a corner of `progress.mentor`, because what she
 * says is a different kind of thing: the mentor answers questions, Iris
 * proposes work and waits to be told whether to do it. The mentor's composer
 * keeps its strings where they are, retitled under her name elsewhere.
 *
 * Three rules every sentence here keeps, and they are the mentor's
 * (AskMentor.tsx) with one added:
 *   1. Each refusal names what stopped the step and what to do next, and no
 *      two refusals share a sentence.
 *   2. None implies the learner did something wrong.
 *   3. None says where the work happens. "Check my work" is one button
 *      (docs/checkpoints.md), and Iris is one helper; a sentence about a
 *      service behind either would make the learner wonder which is real.
 *
 * `Iris` is a name and is written in Latin script in both languages, as Kite is
 * (docs/iris.md "Names", docs/i18n.md). She is never Kite, the course's fictional
 * agent, and nothing here should read as if she were.
 */
export const en = {
  ...learning.en,
  name: 'Iris',
  companion: {
    open: 'Talk to Iris',
    close: 'Close Iris',
    invite: 'Let’s work through it together.',
    ready: 'Ask a question or tell me where you’re stuck.',
    reviewing: 'Compare the proposal with your brief and workspace.',
    showCharacter: 'Show character',
    compactReview: 'Compact review',
    viewWorkspace: 'View workspace',
    moments: 'A moment with Iris',
    hello: 'Say hello',
    breather: 'Take a short break',
  },
  /** The field before anything has been asked. */
  request: 'What should Iris do?',
  start: 'Start',
  /** Under the field, once: an empty field is a real choice, not a mistake. */
  startNote: 'Leave it empty to have Iris start from the brief alone.',
  signedOut: 'Working with Iris needs an account.',
  signIn: 'Sign in',
  /**
   * The approval. Never focused by default and bound to no key: the learner
   * has to reach for it, which is the one thing the panel exists to make sure
   * of.
   */
  runIt: 'Run it',
  reject: 'Reject',
  checkIt: 'Check my work',
  /** The one-line box beside a proposal, and after `done`. */
  tellInstead: 'Or tell Iris instead',
  tellNext: 'Tell Iris what to do next',
  send: 'Send',
  thinking: 'Iris is thinking…',
  running: 'Running…',
  checking: 'Checking your work…',
  /** Screen-reader labels for who is speaking; the paper tells sighted readers. */
  youSaid: 'You said',
  irisSaid: 'Iris',
  /** What became of a step, once the learner decided. */
  decided: {
    ran: 'You ran this.',
    rejected: 'You rejected this.',
    noted: 'You answered instead.',
    checked: 'You checked your work.',
  },
  outputLabel: 'What it printed, as Iris sees it',
  checkLabel: 'What Check my work said, as Iris sees it',
  /** The meter on a level: a count, because nothing runs out here but the account's allowance. */
  tokens: (n: number) => `${num(n, 'en')} ${n === 1 ? 'token' : 'tokens'} in this conversation`,
  /** The meter on a checkpoint: a budget, because it is the end of Iris for this attempt. */
  budget: (used: number, budget: number) => `${num(used, 'en')} / ${num(budget, 'en')} tokens`,
  budgetLabel: "Iris's token budget for this attempt",
  /** Beside Iris on every checkpoint that carries her. Stated, never sprung (docs/iris.md). */
  rule: "On a checkpoint, some of Iris's proposals are wrong on purpose. Approving without checking is how a checkpoint is failed.",
  /** Beside designed agents on a checkpoint (docs/designed-agents.md, section E). */
  checkpointNote: 'On a checkpoint the agents will not finish the job alone. Judge what they propose, and do what is left yourself.',
  /** Levels 5-12: the mentor's explanations, folded under the agent. */
  explainInstead: 'Ask Iris to explain instead',
  /** Beside live Iris on an ordinary level; which levels carry a planted mistake is never said. */
  levelRule: 'Iris is an agent, and agents are sometimes wrong. Before you run a proposal, picture it going wrong and ask what it would break. Then read it for that.',
  /**
   * Scripted Iris on the first levels (ScriptedIris.tsx): her lines are
   * written in advance (docs/designed-agents.md), and some of
   * them are planted. The copy never says so: the rule is the general one.
   */
  scripted: {
    intro: 'Iris can work on this level with you. Do it yourself, let her do it, or both: you decide what runs.',
    rule: 'Iris is an agent, and agents are sometimes wrong, or say one thing and then another. Before you approve a proposal, picture it going wrong and ask what it would break. Then read it for that.',
    start: 'Start with Iris',
    goOn: 'Go on',
    practice: 'Explain your review (optional)',
    reason: 'Your reasoning',
    practiceNote: 'Your note is added to this conversation when you choose an action or reply. It is not assessed and does not change the guided replies.',
    thinkNote: 'Choose a prepared reply or continue. You can record your own reasoning under “Explain your review”.',
    ended: 'Iris has nothing more to propose here. The rest is yours: check your work, or start over with her.',
    startOver: 'Start over with Iris',
    /** Over the replies of a beat that asks for a brief: they are what the learner tells the agent, not an aside. */
    ask: (name: string) => `What do you tell ${name}?`,
    /** The meter when a level sets a budget: it is the scenario's, spent by every agent in it. */
    budgetLabel: 'The agents’ token budget on this level',
  },
  notSaved: 'This conversation is not saved. A reload or a reset starts a new one.',
  runFailed: 'That command could not be run in your workspace. Try it again, or reject it.',
  tryAgain: 'Try again',
  startOver: 'Start a new conversation',
  refusals: {
    'sign-in': 'You are no longer signed in. Sign in, then press Try again.',
    'not-here': "On this level you do the typing. Iris's designed steps cover the opening levels and every checkpoint; the hint ladder below is yours.",
    'not-yours': 'That conversation could not be continued, so a new one has started. Tell Iris what to do.',
    spent: 'This account has used the help available here. The hint ladder is still yours, or carry on in the terminal.',
    budget: "This attempt has reached Iris's token or request limit. Finish the checkpoint yourself.",
    /** A level's conversation is at its step cap: a new conversation lifts it. */
    capped: 'This conversation has reached its limit for requests to Iris. Start a new conversation, or carry on yourself.',
    /** The account's day window (`cap: 'day'`): a new conversation would not lift it, tomorrow does. */
    'capped-day': 'You have reached today’s limit for requests to Iris. Come back tomorrow, or carry on yourself.',
    busy: 'Iris is already working on a step for you, here or in another tab. Wait for it to finish, then press Try again.',
    unavailable: 'Iris could not be reached just now. Press Try again in a moment.',
    filtered: "Iris's answer was held back before it reached you. Press Try again, or tell her more precisely what you want.",
    refused: 'Iris declined that. Tell her what you want in different words.',
  },
};

/*
 * Iris takes feminine agreement throughout. `terminal` and `tokens` stay in
 * Latin script (docs/i18n.md §5: `terminal` always, and a token is the unit
 * the meter counts, the word every model's documentation uses), isolated so
 * the Arabic around them keeps its order. The meter's fraction is isolated as
 * a whole: `1,200 / 300,000` read right to left is a different fraction.
 */
const IRIS = 'Iris';

export const ar: typeof en = {
  ...learning.ar,
  name: IRIS,
  companion: {
    open: `تحدّث مع ${IRIS}`,
    close: `أغلق نافذة ${IRIS}`,
    invite: 'لنفكّر في الأمر معًا.',
    ready: 'اسألني أو أخبرني أين تعثّرت.',
    reviewing: 'قارن الاقتراح بإحاطة المهمة ومساحة عملك.',
    showCharacter: 'أظهر الشخصية',
    compactReview: 'مراجعة مختصرة',
    viewWorkspace: 'اعرض مساحة العمل',
    moments: `لحظة مع ${IRIS}`,
    hello: 'ألقِ التحية',
    breather: 'خذ استراحة قصيرة',
  },
  request: `ماذا تريد أن تفعل ${IRIS}؟`,
  start: 'ابدأ',
  startNote: `اترك الحقل فارغًا لتبدأ ${IRIS} من الإحاطة وحدها.`,
  signedOut: `العمل مع ${IRIS} يحتاج إلى حساب.`,
  signIn: 'سجّل الدخول',
  runIt: 'شغّله',
  reject: 'ارفض',
  checkIt: 'تحقّق من عملي',
  tellInstead: `أو أخبر ${IRIS} بما تريد بدلًا من ذلك`,
  tellNext: `أخبر ${IRIS} بما تفعله بعد ذلك`,
  send: 'أرسل',
  thinking: `${IRIS} تفكّر…`,
  running: 'جارٍ التشغيل…',
  checking: 'جارٍ التحقّق من عملك…',
  youSaid: 'كلماتك',
  irisSaid: IRIS,
  decided: {
    ran: 'شُغِّل هذا الأمر.',
    rejected: 'رُفضت هذه الخطوة.',
    noted: 'أُرسل ردّك بدلًا منها.',
    checked: 'تمّ التحقّق من العمل.',
  },
  outputLabel: `ما طبعه الأمر، كما تراه ${IRIS}`,
  checkLabel: `نتيجة «تحقّق من عملي»، كما تراها ${IRIS}`,
  tokens: (n: number) => `${num(n, 'ar')} من الـ ${ltr('tokens')} في هذه المحادثة`,
  budget: (used: number, budget: number) => `${ltr(`${num(used, 'ar')} / ${num(budget, 'ar')}`)} من الـ ${ltr('tokens')}`,
  budgetLabel: `ميزانية ${IRIS} من الـ ${ltr('tokens')} لهذه المحاولة`,
  rule: `في المحطات، بعض اقتراحات ${IRIS} خاطئة عن قصد. الموافقة عليها دون التحقّق منها هي ما يُفشل المحطة.`,
  checkpointNote: 'في المحطة لن يُنهي الوكلاء العمل وحدهم. احكم على ما يقترحونه، وأنجِز الباقي بنفسك.',
  explainInstead: `اطلب من ${IRIS} أن تشرح بدلًا من ذلك`,
  levelRule: `${IRIS} وكيلة برمجة، والوكلاء يخطئون أحيانًا. قبل أن تشغّل اقتراحًا، تخيّل أنه فشل واسأل ما الذي سيُفسده. ثم اقرأه بحثًا عن ذلك.`,
  scripted: {
    intro: `يمكن أن تعمل ${IRIS} معك على هذا المستوى. أنجِزه بنفسك، أو اتركها تنجزه، أو الاثنين معًا: أنت من يقرّر ما يُنفَّذ.`,
    rule: `${IRIS} وكيلة برمجة، والوكلاء يخطئون أحيانًا، أو يقولون شيئًا ثم عكسه. قبل أن توافق على اقتراح، تخيّل أنه فشل واسأل ما الذي سيُفسده. ثم اقرأه بحثًا عن ذلك.`,
    start: `ابدأ مع ${IRIS}`,
    goOn: 'تابِع',
    practice: 'اشرح مراجعتك (اختياري)',
    reason: 'تفسيرك',
    practiceNote: 'تُضاف ملاحظتك إلى هذه المحادثة عند اختيار إجراء أو رد. لا تُقيَّم ولا تغيّر الردود الإرشادية.',
    thinkNote: 'اختر ردًا جاهزًا أو تابع. يمكنك تسجيل تفسيرك تحت «اشرح مراجعتك».',
    ended: `لم يعد لدى ${IRIS} ما تقترحه هنا. الباقي عليك: تحقّق من عملك، أو ابدأ معها من جديد.`,
    startOver: `ابدأ من جديد مع ${IRIS}`,
    ask: (name: string) => `ماذا تطلب من ${name}؟`,
    budgetLabel: `ميزانية الوكلاء من الـ ${ltr('tokens')} في هذا المستوى`,
  },
  notSaved: 'هذه المحادثة لا تُحفظ. إعادة تحميل الصفحة أو إعادة ضبط المستوى تبدأ محادثة جديدة.',
  runFailed: 'تعذّر تشغيل هذا الأمر في مساحة عملك. جرّبه مجددًا أو ارفضه.',
  tryAgain: 'حاول مجددًا',
  startOver: 'ابدأ محادثة جديدة',
  refusals: {
    'sign-in': 'لم تعد مسجّل الدخول. سجّل الدخول ثم اضغط «حاول مجددًا».',
    'not-here': `في هذا المستوى أنت من يكتب الأوامر. خطوات ${IRIS} المصمَّمة تغطي المستويات الأولى وكل المحطات؛ وسلسلة التلميحات أدناه لك.`,
    'not-yours': `تعذّر إكمال تلك المحادثة، فبدأت محادثة جديدة. أخبر ${IRIS} بما تريد.`,
    spent: `استُخدمت المساعدة المتاحة هنا لهذا الحساب. سلسلة التلميحات ما زالت لك، أو تابع العمل في الـ ${ltr('terminal')}.`,
    budget: `بلغت هذه المحاولة حد ${IRIS} من الـ ${ltr('tokens')} أو الطلبات. أكمل المحطة بنفسك.`,
    capped: `بلغت هذه المحادثة الحد الأقصى للطلبات المرسلة إلى ${IRIS}. ابدأ محادثة جديدة أو تابع بنفسك.`,
    'capped-day': `بلغت الحد اليومي للطلبات المرسلة إلى ${IRIS}. عُد غدًا أو تابع بنفسك.`,
    busy: `${IRIS} تعمل بالفعل على خطوة لك، هنا أو في تبويب آخر. انتظر حتى تنتهي ثم اضغط «حاول مجددًا».`,
    unavailable: `تعذّر الوصول إلى ${IRIS} الآن. اضغط «حاول مجددًا» بعد قليل.`,
    filtered: `حُجب ردّ ${IRIS} قبل أن يصل إليك. اضغط «حاول مجددًا» أو أخبرها بدقة أكبر بما تريد.`,
    refused: `رفضت ${IRIS} هذا الطلب. أخبرها بما تريد بكلمات أخرى.`,
  },
};

export const de: typeof en = {
  ...learning.de,
  name: IRIS,
  companion: {
    open: 'Mit Iris sprechen',
    close: 'Iris schließen',
    invite: 'Gehen wir es gemeinsam an.',
    ready: 'Stell eine Frage oder sag mir, wo du feststeckst.',
    reviewing: 'Vergleiche den Vorschlag mit deinem Auftrag und Arbeitsbereich.',
    showCharacter: 'Figur anzeigen',
    compactReview: 'Kompakte Prüfung',
    viewWorkspace: 'Arbeitsbereich ansehen',
    moments: 'Ein Moment mit Iris',
    hello: 'Hallo sagen',
    breather: 'Eine kurze Pause machen',
  },
  request: 'Was soll Iris tun?',
  start: 'Starten',
  startNote: 'Lass das Feld leer, damit Iris nur vom Auftrag ausgeht.',
  signedOut: 'Für die Arbeit mit Iris brauchst du ein Konto.',
  signIn: 'Anmelden',
  runIt: 'Ausführen',
  reject: 'Ablehnen',
  checkIt: 'Meine Arbeit prüfen',
  tellInstead: 'Oder sag Iris stattdessen etwas',
  tellNext: 'Sag Iris, was als Nächstes kommt',
  send: 'Senden',
  thinking: 'Iris denkt nach …',
  running: 'Läuft …',
  checking: 'Deine Arbeit wird geprüft …',
  youSaid: 'Du hast gesagt',
  irisSaid: IRIS,
  decided: {
    ran: 'Du hast das ausgeführt.',
    rejected: 'Du hast das abgelehnt.',
    noted: 'Du hast stattdessen geantwortet.',
    checked: 'Du hast deine Arbeit geprüft.',
  },
  outputLabel: 'Was es ausgegeben hat, so wie Iris es sieht',
  checkLabel: 'Was „Meine Arbeit prüfen“ gesagt hat, so wie Iris es sieht',
  tokens: (n) => `${num(n, 'de')} ${n === 1 ? 'Token' : 'Tokens'} in diesem Gespräch`,
  budget: (used, budget) => `${num(used, 'de')} / ${num(budget, 'de')} Tokens`,
  budgetLabel: 'Das Token-Budget von Iris für diesen Versuch',
  rule: 'In einem Checkpoint sind manche Vorschläge von Iris absichtlich falsch. Wer ohne Prüfen zustimmt, fällt durch den Checkpoint.',
  checkpointNote: 'In einem Checkpoint erledigen die Agenten die Arbeit nicht allein. Beurteile, was sie vorschlagen, und mach den Rest selbst.',
  explainInstead: 'Lass dir stattdessen von Iris etwas erklären',
  levelRule: 'Iris ist ein Agent, und Agenten liegen manchmal falsch. Bevor du einen Vorschlag ausführst, stell dir vor, er geht schief, und frag, was er kaputt machen würde. Dann lies ihn genau darauf hin.',
  scripted: {
    intro: 'Iris kann dieses Level mit dir bearbeiten. Mach es selbst, lass Iris es machen oder beides: Du entscheidest, was läuft.',
    rule: 'Iris ist ein Agent, und Agenten liegen manchmal falsch oder sagen erst das eine und dann das andere. Bevor du einem Vorschlag zustimmst, stell dir vor, er geht schief, und frag, was er kaputt machen würde. Dann lies ihn genau darauf hin.',
    start: 'Mit Iris anfangen',
    goOn: 'Weiter',
    practice: 'Erkläre deine Prüfung (optional)',
    reason: 'Deine Begründung',
    practiceNote: 'Deine Notiz wird diesem Gespräch hinzugefügt, wenn du eine Aktion oder Antwort wählst. Sie wird nicht bewertet und ändert die vorgegebenen Antworten nicht.',
    thinkNote: 'Wähle eine vorgegebene Antwort oder fahre fort. Unter „Erkläre deine Prüfung“ kannst du deine eigene Begründung festhalten.',
    ended: 'Iris hat hier nichts mehr vorzuschlagen. Der Rest liegt bei dir: Prüfe deine Arbeit oder fang mit ihr neu an.',
    startOver: 'Mit Iris neu anfangen',
    ask: (name) => `Was sagst du ${name}?`,
    budgetLabel: 'Das Token-Budget der Agenten in diesem Level',
  },
  notSaved: 'Dieses Gespräch wird nicht gespeichert. Neu laden oder Zurücksetzen beginnt ein neues.',
  runFailed: 'Dieser Befehl konnte in deinem Arbeitsbereich nicht ausgeführt werden. Versuch es noch einmal oder lehne ihn ab.',
  tryAgain: 'Erneut versuchen',
  startOver: 'Neues Gespräch beginnen',
  refusals: {
    'sign-in': 'Du bist nicht mehr angemeldet. Melde dich an und drück dann „Erneut versuchen“.',
    'not-here': 'In diesem Level tippst du selbst. Die vorbereiteten Schritte von Iris gibt es in den ersten Levels und in jedem Checkpoint; die Hinweisleiter unten gehört dir.',
    'not-yours': 'Dieses Gespräch konnte nicht fortgesetzt werden, deshalb hat ein neues begonnen. Sag Iris, was sie tun soll.',
    spent: 'Dieses Konto hat die hier verfügbare Hilfe aufgebraucht. Die Hinweisleiter gehört weiter dir, oder mach im Terminal weiter.',
    budget: 'Dieser Versuch hat das Token- oder Anfragelimit von Iris erreicht. Bring den Checkpoint selbst zu Ende.',
    capped: 'Dieses Gespräch hat die maximale Anzahl an Anfragen an Iris erreicht. Beginne ein neues Gespräch oder mach selbst weiter.',
    'capped-day': 'Du hast das heutige Limit für Anfragen an Iris erreicht. Komm morgen wieder oder mach selbst weiter.',
    busy: 'Iris arbeitet schon an einem Schritt für dich, hier oder in einem anderen Tab. Warte, bis er fertig ist, und drück dann „Erneut versuchen“.',
    unavailable: 'Iris war gerade nicht erreichbar. Drück gleich „Erneut versuchen“.',
    filtered: 'Die Antwort von Iris wurde zurückgehalten, bevor sie dich erreicht hat. Drück „Erneut versuchen“ oder sag ihr genauer, was du willst.',
    refused: 'Iris hat das abgelehnt. Sag ihr mit anderen Worten, was du willst.',
  },
};

export const ja: typeof en = {
  ...learning.ja,
  name: 'Iris',
  companion: {
    open: 'Irisに話しかける',
    close: 'Irisを閉じる',
    invite: '一緒に考えていきましょう。',
    ready: '質問してください。行き詰まっているところを教えてもらっても大丈夫です。',
    reviewing: '提案をブリーフと作業環境に照らして確認してください。',
    showCharacter: 'キャラクターを表示',
    compactReview: 'コンパクトなレビュー',
    viewWorkspace: '作業画面を見る',
    moments: 'Irisとひと息',
    hello: 'あいさつする',
    breather: '少し休憩する',
  },
  request: 'Irisに何をしてもらいますか？',
  start: '開始',
  startNote: '空のままにすると、Irisはブリーフだけを手がかりに始めます。',
  signedOut: 'Irisと作業するにはアカウントが必要です。',
  signIn: 'ログイン',
  runIt: '実行',
  reject: '却下',
  checkIt: '作業をチェック',
  tellInstead: '代わりにIrisに伝える',
  tellNext: '次にやることをIrisに伝える',
  send: '送信',
  thinking: 'Irisが考えています…',
  running: '実行中…',
  checking: '作業をチェックしています…',
  youSaid: 'あなた',
  irisSaid: 'Iris',
  decided: {
    ran: 'これを実行しました。',
    rejected: 'これを却下しました。',
    noted: '代わりに返信しました。',
    checked: '作業をチェックしました。',
  },
  outputLabel: '出力された内容(Irisに見えているもの)',
  checkLabel: '「作業をチェック」の結果(Irisに見えているもの)',
  tokens: (n: number) => `この会話で${num(n, 'ja')}トークン`,
  budget: (used: number, budget: number) => `${num(used, 'ja')} / ${num(budget, 'ja')}トークン`,
  budgetLabel: 'この挑戦でIrisが使えるトークン予算',
  rule: 'チェックポイントでは、Irisの提案のいくつかは、わざと間違っています。確認せずに承認すると、チェックポイントに不合格になります。',
  checkpointNote: 'チェックポイントでは、エージェントだけで作業は終わりません。提案を見極め、残りは自分で仕上げてください。',
  explainInstead: '代わりにIrisに説明してもらう',
  levelRule: 'Irisはエージェントで、エージェントは時々間違えます。提案を実行する前に、それが失敗したと想像して、何が壊れるかを考えてください。そのうえで、その点に注目して読んでください。',
  scripted: {
    intro: 'このレベルはIrisと一緒に進められます。自分で進めても、Irisに任せても、両方でもかまいません。何を実行するかを決めるのはあなたです。',
    rule: 'Irisはエージェントで、エージェントは時々間違えたり、言うことが変わったりします。提案を承認する前に、それが失敗したと想像して、何が壊れるかを考えてください。そのうえで、その点に注目して読んでください。',
    start: 'Irisと始める',
    goOn: '続ける',
    practice: '判断の理由を書く（任意）',
    reason: 'あなたの理由',
    practiceNote: '操作または返信を選ぶと、この会話にメモが追加されます。採点はされず、用意された返信は変わりません。',
    thinkNote: '用意された返信を選ぶか、続けてください。「判断の理由を書く」で自分の理由を記録できます。',
    ended: 'Irisがここで提案することはもうありません。あとはあなた次第です。作業を確認するか、Irisと最初からやり直してください。',
    startOver: 'Irisと最初からやり直す',
    ask: (name: string) => `${name}に何と伝えますか？`,
    budgetLabel: 'このレベルでエージェントが使えるトークン予算',
  },
  notSaved: 'この会話は保存されません。再読み込みやリセットをすると、新しい会話になります。',
  runFailed: 'そのコマンドをワークスペースで実行できませんでした。もう一度試すか、却下してください。',
  tryAgain: 'もう一度試す',
  startOver: '新しい会話を始める',
  refusals: {
    'sign-in': 'ログアウトされています。ログインしてから「もう一度試す」を押してください。',
    'not-here': 'このレベルでは、入力するのはあなたです。Irisの用意されたステップがあるのは、最初のレベルとすべてのチェックポイントです。下のヒントの段階は、あなたのものです。',
    'not-yours': 'その会話は続けられなかったため、新しい会話を始めました。Irisに何をしてほしいか伝えてください。',
    spent: 'このアカウントは、ここで使えるヘルプを使い切りました。ヒントの段階は引き続き使えます。ターミナルで進めることもできます。',
    budget: 'この挑戦では、Irisのトークン数またはリクエスト回数の上限に達しました。チェックポイントは自分で仕上げてください。',
    capped: 'この会話では、Irisへのリクエスト回数の上限に達しました。新しい会話を始めるか、自分で進めてください。',
    'capped-day': 'Irisへのリクエスト回数が今日の上限に達しました。明日また来るか、自分で進めてください。',
    busy: 'Irisはすでに、ここか別のタブであなたのためのステップに取り組んでいます。終わるのを待ってから、「もう一度試す」を押してください。',
    unavailable: 'Irisに接続できませんでした。少し待ってから「もう一度試す」を押してください。',
    filtered: 'Irisの返答は、あなたに届く前に保留されました。「もう一度試す」を押すか、してほしいことをもっと具体的に伝えてください。',
    refused: 'Irisはそれを断りました。ほかの言い方で、してほしいことを伝えてください。',
  },
};
